import React, { useState, useEffect, useMemo } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, PencilSimple, Power, GitMerge, Warning, MapPin } from '@phosphor-icons/react';
import SearchableSelect from '../../components/SearchableSelect';
import SuperAdminDeleteButton from '../../components/SuperAdminDeleteButton';

function Badge({ tone, children }) {
  const map = {
    active: 'bg-[#00C853]/10 text-[#00C853]',
    inactive: 'bg-[#FF3333]/10 text-[#FF3333]',
    orphan: 'bg-[#FF8C00]/10 text-[#FF8C00]',
    count: 'bg-[#002FA7]/10 text-[#002FA7]',
    zero: 'bg-[#F3F3F3] text-[#999]',
  };
  return (
    <span className={`text-xs uppercase tracking-widest px-2 py-1 ${map[tone] || map.count}`}>
      {children}
    </span>
  );
}

export default function HaltLocationMaster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '' });
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const fetchRows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/halt-locations-master');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => r.name.toLowerCase().includes(q));
  }, [rows, search]);

  const totals = useMemo(() => ({
    total: rows.length,
    orphans: rows.filter(r => r.is_orphan).length,
    withDupes: rows.filter(r => (r.similar_names || []).length > 0).length,
    inactive: rows.filter(r => r.status === 'inactive').length,
  }), [rows]);

  const resetForm = () => { setForm({ name: '' }); setEditing(null); setError(''); };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name });
    setError('');
    setDialogOpen(true);
  };

  const openPromote = (row) => {
    // Orphan — pre-fill create form with its name so it becomes canonical
    setEditing(null);
    setForm({ name: row.name });
    setError('');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      if (editing && editing.id) {
        await api.put(`/admin/halt-locations-master/${editing.id}`, { name: form.name });
      } else {
        await api.post('/admin/halt-locations-master', { name: form.name });
      }
      setDialogOpen(false); resetForm(); await fetchRows();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (row) => {
    if (!row.id) return;
    setBusy(true);
    try {
      if (row.status === 'active') {
        await api.delete(`/admin/halt-locations-master/${row.id}`);
      } else {
        await api.put(`/admin/halt-locations-master/${row.id}`, { status: 'active' });
      }
      await fetchRows();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const openMerge = (row) => {
    setMergeSource(row);
    // Default target: first similar name if available, else blank
    const similar = (row.similar_names || [])[0] || '';
    setMergeTarget(similar);
    setError('');
    setMergeOpen(true);
  };

  const handleMerge = async (e) => {
    e.preventDefault();
    if (!mergeSource || !mergeTarget || mergeTarget.toLowerCase() === mergeSource.name.toLowerCase()) {
      setError('Pick a different target');
      return;
    }
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/admin/halt-locations-master/merge', {
        from_name: mergeSource.name,
        to_name: mergeTarget,
      });
      setMergeOpen(false);
      setMergeSource(null);
      await fetchRows();
      const moved = data.moved || {};
      alert(`Merged into "${data.target}". Moved: ${moved.pilots || 0} pilot(s), ${moved.loadmen || 0} helper(s), ${moved.geofences || 0} geofence(s).`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const activeTargetOptions = rows
    .filter(r => r.status === 'active' && !r.is_orphan && (!mergeSource || r.name.toLowerCase() !== mergeSource.name.toLowerCase()))
    .map(r => ({ value: r.name, label: r.name }));

  return (
    <div data-testid="halt-master-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Halt Locations</h1>
          <p className="text-sm text-[#666] mt-1">Canonical list of halts used by pilots, helpers and geofences. Merge duplicates to keep data clean.</p>
        </div>
        <Button data-testid="add-halt-btn" onClick={openCreate} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 rounded-none">
          <Plus size={16} className="mr-2" /> Add Halt
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E5E5E5] p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#666]">Total</p>
          <p className="text-2xl font-black text-[#0A0A0A]">{totals.total}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#666]">Orphans</p>
          <p className={`text-2xl font-black ${totals.orphans ? 'text-[#FF8C00]' : 'text-[#999]'}`}>{totals.orphans}</p>
          <p className="text-[10px] text-[#666] mt-0.5">Referenced but not registered</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#666]">Likely Duplicates</p>
          <p className={`text-2xl font-black ${totals.withDupes ? 'text-[#FF3333]' : 'text-[#999]'}`}>{totals.withDupes}</p>
          <p className="text-[10px] text-[#666] mt-0.5">Same name ignoring case/spaces</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#666]">Inactive</p>
          <p className="text-2xl font-black text-[#0A0A0A]">{totals.inactive}</p>
        </div>
      </div>

      <Input
        data-testid="halt-search-input"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search halt name..."
        className="rounded-none max-w-md"
      />

      <div className="bg-white border border-[#E5E5E5]">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-center">Pilots</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-center">Helpers</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-center">Geofences</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-[#666] py-8">Loading...</TableCell></TableRow>
            )}
            {!loading && filtered.map((r, i) => {
              const hasDupes = (r.similar_names || []).length > 0;
              return (
                <TableRow key={(r.id || r.name) + i} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''} data-testid={`halt-row-${r.name}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-[#002FA7] shrink-0" weight="bold" />
                      <span className="font-medium">{r.name}</span>
                      {r.is_orphan && <Badge tone="orphan">Orphan</Badge>}
                      {hasDupes && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#FF3333]/10 text-[#FF3333] px-1.5 py-0.5" title={`Similar: ${r.similar_names.join(', ')}`}>
                          <Warning size={11} weight="bold" /> Dupe
                        </span>
                      )}
                    </div>
                    {hasDupes && (
                      <p className="text-[11px] text-[#FF3333]/80 mt-1">Similar: {r.similar_names.join(', ')}</p>
                    )}
                  </TableCell>
                  <TableCell className="text-center"><Badge tone={r.pilots_count ? 'count' : 'zero'}>{r.pilots_count}</Badge></TableCell>
                  <TableCell className="text-center"><Badge tone={r.loadmen_count ? 'count' : 'zero'}>{r.loadmen_count}</Badge></TableCell>
                  <TableCell className="text-center"><Badge tone={r.geofences_count ? 'count' : 'zero'}>{r.geofences_count}</Badge></TableCell>
                  <TableCell>
                    <Badge tone={r.status === 'inactive' ? 'inactive' : 'active'}>
                      {r.status === 'inactive' ? 'Inactive' : 'Active'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      {r.is_orphan ? (
                        <button
                          onClick={() => openPromote(r)}
                          className="px-2 py-1 text-[10px] uppercase tracking-widest bg-[#002FA7] text-white hover:bg-[#002FA7]/90"
                          title="Register this orphan into the master"
                          data-testid={`halt-promote-${r.name}`}
                        >
                          Register
                        </button>
                      ) : (
                        <>
                          <button onClick={() => openEdit(r)} className="p-2 hover:bg-[#F3F3F3]" title="Rename" data-testid={`halt-edit-${r.id}`}>
                            <PencilSimple size={16} />
                          </button>
                          <button
                            onClick={() => handleToggle(r)}
                            className={`p-2 transition-colors ${r.status === 'active' ? 'hover:bg-[#FF3333]/10 text-[#FF3333]' : 'hover:bg-[#00C853]/10 text-[#00C853]'}`}
                            title={r.status === 'active' ? 'Deactivate' : 'Activate'}
                            disabled={busy}
                            data-testid={`halt-toggle-${r.id}`}
                          >
                            <Power size={16} weight={r.status === 'active' ? 'regular' : 'bold'} />
                          </button>
                          <SuperAdminDeleteButton masterType="halt-locations" recordId={r.id} recordName={r.name} onDeleted={fetchRows} />
                        </>
                      )}
                      <button
                        onClick={() => openMerge(r)}
                        className="p-2 hover:bg-[#8E44AD]/10 text-[#8E44AD]"
                        title="Merge into another halt"
                        data-testid={`halt-merge-${r.name}`}
                      >
                        <GitMerge size={16} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-[#666] py-8">No halt locations found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add / Rename dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Rename Halt' : 'Add Halt'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {editing && (
              <p className="text-xs text-[#666]">Renaming will update this halt across <strong>{editing.pilots_count}</strong> pilot(s), <strong>{editing.loadmen_count}</strong> helper(s) and <strong>{editing.geofences_count}</strong> geofence(s).</p>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest">Halt Name *</Label>
              <Input
                data-testid="halt-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                autoFocus
                className="rounded-none"
                placeholder="e.g. Unit 1"
              />
            </div>
            {error && <p className="text-xs text-[#FF3333]">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none">Cancel</Button>
              <Button
                data-testid="halt-save-btn"
                type="submit"
                disabled={busy}
                className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 rounded-none"
              >
                {editing ? 'Rename' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Merge dialog */}
      <Dialog open={mergeOpen} onOpenChange={(o) => { setMergeOpen(o); if (!o) { setMergeSource(null); setError(''); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Merge Halt Location</DialogTitle></DialogHeader>
          {mergeSource && (
            <form onSubmit={handleMerge} className="space-y-4 mt-2">
              <div className="bg-[#F3F3F3] p-3">
                <p className="text-[10px] uppercase tracking-widest text-[#666]">Source (will be deactivated)</p>
                <p className="text-sm font-bold">{mergeSource.name}</p>
                <p className="text-[11px] text-[#666] mt-1">
                  Currently attached: {mergeSource.pilots_count} pilot(s) · {mergeSource.loadmen_count} helper(s) · {mergeSource.geofences_count} geofence(s)
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Merge Into *</Label>
                <SearchableSelect
                  value={mergeTarget}
                  onChange={(v) => setMergeTarget(v)}
                  options={activeTargetOptions}
                  placeholder="Select target halt"
                  searchPlaceholder="Search halts..."
                  emptyText="No active halts to merge into"
                  testId="halt-merge-target"
                />
                <p className="text-[11px] text-[#666] mt-1">All pilots, helpers and geofences pointing at <strong>{mergeSource.name}</strong> will be rewritten to the selected halt.</p>
              </div>
              {error && <p className="text-xs text-[#FF3333]">{error}</p>}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setMergeOpen(false)} className="rounded-none">Cancel</Button>
                <Button
                  data-testid="halt-merge-confirm"
                  type="submit"
                  disabled={busy || !mergeTarget}
                  className="bg-[#8E44AD] text-white hover:bg-[#8E44AD]/90 rounded-none"
                >
                  Merge
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
